import React from 'react';

import { FormattedMessage } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { getDowngradeConfirmationContent } from './billing-plan-downgrade-confirmation-modal-content-map';
import { ModalPortal } from '../../../../../../components/modal-portal';
import { useGetNumberOfIncludedUserSeatsForPlan } from '../../../hooks/use-plan-details';

import { messages } from './messages';

import style from './billing-plan-downgrade-confirmation-modal.module.less';

/**
 * @param {object} props
 * @param {string} props.currentBillingPlanId
 * @param {string} props.selectedBillingPlanId
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onClose
 * @returns {React.JSX.Element|null}
 */
export const BillingPlanDowngradeConfirmationModal = ({ currentBillingPlanId, selectedBillingPlanId, onConfirm, onClose }) => {
  const currentPlanIncludedUsers = useGetNumberOfIncludedUserSeatsForPlan(currentBillingPlanId);
  const selectedPlanIncludedUsers = useGetNumberOfIncludedUserSeatsForPlan(selectedBillingPlanId);

  const content = getDowngradeConfirmationContent(selectedBillingPlanId);
  if (!content) {
    return null;
  }

  const { bannerMessage, primaryMessage, featureMessages } = content;

  const includedUserDifference = currentPlanIncludedUsers - selectedPlanIncludedUsers;

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          <Text className={style['modal-header']} textType={TextType.Description} value={messages.confirmDowngradeTitle} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          {bannerMessage && (
            <BannerMessageV2
              content={<Text textType={TextType.Caption} value={bannerMessage} />}
              layout={BannerLayout.Inline}
              status={BannerStatus.Error}
            />
          )}
          <div>
            <Text className={style['primary-message']} element='p' textType={TextType.Label} value={primaryMessage} />
            {Boolean(featureMessages?.length) && (
              <ul className={style['feature-list']}>
                {featureMessages.map((featureMessage) => (
                  <li key={featureMessage.id} className={style['feature-item']}>
                    <Text
                      formattedMessageValues={{
                        userCount: includedUserDifference,
                      }}
                      textType={TextType.Paragraph}
                      value={featureMessage}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton />
          <Button onClick={onConfirm} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.downgradeButtonText} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
